import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, DynamicIconWrapper, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { convertBytesToSize } from '@utils/fileSizeFormatter';
import { formatDate } from '@utils/kpi-utils/date';

import { IAssetRentRequestDocument } from '@interfaces/rent/assetRent';

import { formatTemplateWithTime, timezoneUTC } from '@constants/common';
import { columnSizes } from '@constants/table';
import {
	deleteDocumentTooltip,
	descriptionDocumentTooltip,
	downloadDocumentTooltip,
	EAttachedDocumentsColumnsAccessors,
	editDocumentTooltip,
	MAX_DESCRIPTION_ROW_TEXT_LENGTH,
} from './constants';

import styles from './AttachedContractDocuments.module.scss';

interface IAttachedDocumentsColumns {
	setSelectedRow: (row: IAssetRentRequestDocument) => void;
	openDeleteModal: () => void;
	openDescriptionModal: () => void;
	downloadFile: (fileId: string) => void;
	openUploadModal: () => void;
	isEditAccess: boolean;
	isAo: boolean;
}

export const getColumns = ({
	setSelectedRow,
	openDeleteModal,
	openDescriptionModal,
	downloadFile,
	openUploadModal,
	isEditAccess,
	isAo,
}: IAttachedDocumentsColumns) => [
	{
		header: 'File name',
		accessorKey: EAttachedDocumentsColumnsAccessors.file,
		size: columnSizes.size250,
		cell: ({ row: { original } }: CellContext<IAssetRentRequestDocument, any>) => {
			const { file } = original;

			return (
				<AdaptiveTableCell
					maxTextWidth={MAX_DESCRIPTION_ROW_TEXT_LENGTH}
					text={file?.name}
				/>
			);
		},
	},
	{
		header: 'Size',
		size: columnSizes.size100,
		accessorKey: EAttachedDocumentsColumnsAccessors.size,
		enableSorting: false,
		cell: ({ row: { original } }: CellContext<IAssetRentRequestDocument, any>) => {
			const { file } = original;

			return <AdaptiveTableCell text={convertBytesToSize(file?.size)} />;
		},
	},
	{
		header: 'Updated',
		size: columnSizes.size150,
		accessorKey: EAttachedDocumentsColumnsAccessors.uploaded,
		cell: ({ row: { original } }: CellContext<IAssetRentRequestDocument, any>) => {
			const { author } = original;

			const utcDate = utcToZonedTime(
				original.updatedAt,
				timezoneUTC,
			);

			const date = formatDate(utcDate, formatTemplateWithTime);

			const text = isAo ? author : date;
			const subText = isAo ? date : undefined;

			return (
				<AdaptiveTableCell
					maxTextWidth={MAX_DESCRIPTION_ROW_TEXT_LENGTH}
					subText={subText}
					text={text}
				/>
			);
		},
	},
	{
		header: 'Description',
		accessorKey: EAttachedDocumentsColumnsAccessors.description,
		enableSorting: false,
		cell: ({ getValue, row }: CellContext<IAssetRentRequestDocument, any>) => {
			const value = getValue();
			const onClick = () => {
				if (!row?.original?.description) return;

				setSelectedRow(row?.original);
				openDescriptionModal();
			};

			return value
				? (
					<div className={styles.descriptionRow}>
						<DynamicIconWrapper
							icon={Icons.description}
							onClick={onClick}
							tooltipLabel={descriptionDocumentTooltip}
						/>
						<AdaptiveTableCell
							isWithoutTooltip
							maxTextWidth={MAX_DESCRIPTION_ROW_TEXT_LENGTH}
							text={value}
						/>
					</div>
				)
				: null;
		},
	},
	{
		header: 'Actions',
		accessorKey: EAttachedDocumentsColumnsAccessors.actions,
		size: columnSizes.size85,
		enableSorting: false,
		cell: ({ row }: CellContext<IAssetRentRequestDocument, any>) => {
			const fileId = row?.original?.file?.id;
			const selectedRow = row?.original;
			const actionAccess = isAo && isEditAccess && fileId;

			const handleDelete = () => {
				if (!selectedRow?.id) return;

				setSelectedRow(selectedRow);
				openDeleteModal();
			};

			const handleEdit = () => {
				if (!fileId) return;

				setSelectedRow(selectedRow);
				openUploadModal();
			};

			const handleDownload = () => {
				if (fileId) downloadFile(fileId);
			};

			const gridCount = [!!actionAccess, !!fileId, !!actionAccess].filter(Boolean).length;

			return (
				<Actions
					deleteTooltip={deleteDocumentTooltip}
					downloadHandler={handleDownload}
					downloadTooltip={downloadDocumentTooltip}
					editHandler={handleEdit}
					editTooltip={editDocumentTooltip}
					gridNumberOfColumns={gridCount}
					handleDelete={handleDelete}
					isDelete={!!actionAccess}
					isDownload={!!fileId}
					isEdit={!!actionAccess}
				/>
			);
		},
	},
];
